import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Button, Spinner, Table } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { RegisterAtttendance } from "../../../services/employeeApi.js";
import { toast } from "react-toastify";
const RegisterAttendanceForm = ({ attendanceData, onSubmitButton }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const employeeList = Array.isArray(attendanceData)
    ? attendanceData
    : Object.values(attendanceData);
  const [attendanceStatus, setAttendanceStatus] = useState(
    employeeList.reduce((acc, emp) => {
      acc[emp.id] = "present";
      return acc;
    }, {})
  );
  const registrationMutation = useMutation(async (newData) => {
    const response = await RegisterAtttendance(newData);
    queryClient.invalidateQueries("branch-attendance");
    return response;
  });
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
  });

  const handleStatusChange = (status, worker_id) => {
    setAttendanceStatus((prev) => ({
      ...prev,
      [worker_id]: prev[worker_id] === status ? "" : status, // toggle off if same clicked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      attendances: employeeList.map((emp) => {
        const status = attendanceStatus[emp.id] || "absent";
        let check_in_time = null;
        let check_out_time = null;
        if (status === "present") {
          check_in_time = `${formData.date}T08:00:00`;
          check_out_time = `${formData.date}T16:00:00`;
        } else if (status === "late") {
          check_in_time = `${formData.date}T09:00:00`;
          check_out_time = `${formData.date}T16:00:00`;
        }
        return {
          worker_id: emp.id,
          date: formData.date,
          status,
          check_in_time,
          check_out_time,
        };
      }),
    };

    try {
      setIsSubmitting(true);
      const result = await registrationMutation.mutateAsync(payload);
      if (result.status === "Success") {
        if (result.created > 0 && result.skipped > 0) {
          toast.info(
            `${result.created} attendance(s) recorded successfully. ${result.skipped} skipped (already recorded).`,
            { position: "top-right" }
          );
        } else if (result.created > 0) {
          toast.success(
            `${result.created} attendance(s) recorded successfully!`,
            {
              position: "top-right",
            }
          );
        } else if (result.skipped > 0) {
          toast.warn(
            `${result.skipped} attendance(s) skipped (already recorded).`,
            {
              position: "top-right",
            }
          );
        } else {
          toast.info("No new attendance records were added.", {
            position: "top-right",
          });
        }
      } else {
        toast.error("Something went wrong while recording attendance.", {
          position: "top-right",
        });
      }
      handleCloseModal();
      history.push(`/branch-view`);
    } catch (error) {
      toast.error(error.message || "Failed to record attendance", {
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    onSubmitButton();
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, date: e.target.value });
  };
  return (
    <Form onSubmit={handleSubmit}>
      <div className="col-md-12 mb-3">
        <Form.Label className="fw-bold text-muted">{t("Date")}</Form.Label>
        <Form.Control
          type="date"
          required
          value={formData.date}
          onChange={handleInputChange}
          max={new Date().toISOString().split("T")[0]}
        />
      </div>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>{t("Employee Name")}</th>
            <th>{t("Attendance Status")}</th>
          </tr>
        </thead>
        <tbody>
          {employeeList.map((emp, index) => (
            <tr key={emp.id}>
              <td>{index + 1}</td>
              <td className="fw-semibold">
                {emp.first_name} {emp.father_name}
              </td>
              <td>
                <div className="d-flex flex-wrap gap-2">
                  {["present", "absent", "late"].map((status) => (
                    <Form.Check
                      key={status}
                      type="checkbox"
                      id={`${emp.id}-${status}`}
                      label={t(
                        status.charAt(0).toUpperCase() +
                          status.slice(1).replace("_", " ")
                      )}
                      checked={attendanceStatus[emp.id] === status}
                      onChange={() => handleStatusChange(status, emp.id)}
                      inline
                    />
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex mt-3 justify-content-end">
        <Button
          className="me-2 btn-secondary"
          onClick={handleCloseModal}
          disabled={isSubmitting}
        >
          {t("Cancel")}
        </Button>
        &nbsp; &nbsp; &nbsp;
        <Button className="btn-info" type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner
                as="span"
                animation="grow"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              {t("Submitting...")}
            </>
          ) : (
            t("Register")
          )}
        </Button>
      </div>
    </Form>
  );
};

export default RegisterAttendanceForm;
